import { HUD_DEGRADATION } from '../config/tuning';
import { clamp } from '../flight/Aerodynamics';

/**
 * HUD degradation under EW jamming (SPEC §3.4.2).
 *
 * The HUD is not hidden when jammed — it fails in specific, legible ways. Bearing tags drift and
 * then drop out, the heading becomes unreliable and is struck through, and the flight path marker
 * jitters. What is left is the horizon and the terrain, which is the intended difficulty curve of
 * the late campaign: the player has to fly on the picture rather than on the symbology.
 *
 * The noise is a closed-form function of (channel, time), not a stateful PRNG, so two runs at the
 * same tick produce the same wobble and a replay looks identical. Deterministic to the pixel.
 */

const DEG_TO_RAD = Math.PI / 180;
const TAG_DRIFT_MAX_RAD = HUD_DEGRADATION.tagDriftMaxDeg * DEG_TO_RAD;
const HEADING_DRIFT_MAX_RAD = HUD_DEGRADATION.headingDriftMaxDeg * DEG_TO_RAD;

/**
 * Smooth, deterministic noise in [-1, 1]. Two incommensurable sine terms per channel: enough
 * structure to read as interference, cheap enough to evaluate per tag per frame, and stateless.
 */
export function degradationNoise(channel: number, timeS: number, frequencyHz: number): number {
  const phase = channel * 12.9898;
  const a = Math.sin(timeS * frequencyHz * 6.2831853 + phase);
  const b = Math.sin(timeS * frequencyHz * 10.9955743 + phase * 1.7 + 2.399);
  return a * 0.62 + b * 0.38;
}

export class HudDegradation {
  /** 0 = clean, 1 = fully jammed. */
  jamming = 0;
  private timeS = 0;

  /** Sample the degradation for this frame. */
  update(jamming: number, simTimeS: number): void {
    this.jamming = clamp(jamming, 0, 1);
    this.timeS = simTimeS;
  }

  /** True once the heading readout can no longer be trusted; drawn with a strike-through. */
  get headingUnreliable(): boolean {
    return this.jamming >= HUD_DEGRADATION.headingUnreliableAt;
  }

  /** Heading error to add to the displayed heading, radians. */
  get headingErrorRad(): number {
    if (!this.headingUnreliable) return 0;
    const severity = rampFrom(this.jamming, HUD_DEGRADATION.headingUnreliableAt);
    return degradationNoise(7, this.timeS, HUD_DEGRADATION.driftFrequencyHz) *
      HEADING_DRIFT_MAX_RAD *
      severity;
  }

  /** Flight path marker jitter, in 1080p pixels, x and y. */
  fpmJitterX(): number {
    return (
      degradationNoise(11, this.timeS, HUD_DEGRADATION.jitterFrequencyHz) *
      HUD_DEGRADATION.fpmJitterMaxPx *
      this.jamming
    );
  }

  fpmJitterY(): number {
    return (
      degradationNoise(13, this.timeS, HUD_DEGRADATION.jitterFrequencyHz) *
      HUD_DEGRADATION.fpmJitterMaxPx *
      this.jamming
    );
  }

  /** Azimuth error to add to a bearing tag, radians. */
  tagDriftRad(tagIndex: number): number {
    if (this.jamming < HUD_DEGRADATION.tagDriftStart) return 0;
    const severity = rampFrom(this.jamming, HUD_DEGRADATION.tagDriftStart);
    return (
      degradationNoise(tagIndex * 3 + 1, this.timeS, HUD_DEGRADATION.driftFrequencyHz) *
      TAG_DRIFT_MAX_RAD *
      severity
    );
  }

  /**
   * True when a tag has dropped out entirely. Tags fail one at a time rather than all at once,
   * so the player sees the display decaying rather than blinking off.
   */
  tagDropped(tagIndex: number): boolean {
    if (this.jamming < HUD_DEGRADATION.tagDropStart) return false;
    const severity = rampFrom(this.jamming, HUD_DEGRADATION.tagDropStart);
    const flicker = degradationNoise(
      tagIndex * 5 + 2,
      this.timeS,
      HUD_DEGRADATION.driftFrequencyHz * 2.3,
    );
    return flicker < severity * 2 - 1;
  }
}

/** Rescale [threshold, 1] onto [0, 1]. */
function rampFrom(value: number, threshold: number): number {
  if (threshold >= 1) return 0;
  return clamp((value - threshold) / (1 - threshold), 0, 1);
}
